import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, Printer, Users, CheckCircle2, AlertTriangle, XCircle, ShieldOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffEvaluationSummaryTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/compliance/staff-evaluation-status`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load staff evaluation status");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handlePrint = () => {
    if (!data) return;
    const now = new Date();
    const dateStr = now.toLocaleDateString("en-US", { month: "long", year: "numeric", day: "numeric" });

    const statusOrder = { missing: 0, expired: 1, expiring: 2, compliant: 3, exempt: 4 };
    const sorted = [...data.staff].sort((a, b) => (statusOrder[a.status] || 0) - (statusOrder[b.status] || 0));

    const statusLabel = (s) => {
      if (s === "compliant") return '<span style="color:#16a34a;font-weight:600">Current</span>';
      if (s === "expiring") return '<span style="color:#d97706;font-weight:600">Expiring Soon</span>';
      if (s === "expired") return '<span style="color:#dc2626;font-weight:600">Expired</span>';
      if (s === "exempt") return '<span style="color:#6b7280;font-weight:600">Exempt</span>';
      return '<span style="color:#dc2626;font-weight:600">Missing</span>';
    };

    const rows = sorted.map(s => `
      <tr>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb">${s.staffName}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb">${s.role || "Staff"}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb">${s.evaluationDate || "N/A"}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb">${s.expirationDate || "N/A"}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb">${statusLabel(s.status)}</td>
      </tr>
    `).join("");

    const html = `
      <html><head><title>Staff Evaluation Compliance Report</title></head>
      <body style="font-family:Arial,sans-serif;max-width:900px;margin:0 auto;padding:20px">
        <h2 style="text-align:center;color:#1e3a5f">Staff Evaluation Compliance Report</h2>
        <p style="text-align:center;color:#666">Generated: ${dateStr}</p>
        <div style="display:flex;justify-content:center;gap:30px;margin:20px 0">
          <div style="text-align:center"><strong style="font-size:24px;color:#16a34a">${data.compliantCount}</strong><br/>Current</div>
          <div style="text-align:center"><strong style="font-size:24px;color:#d97706">${data.expiringCount || 0}</strong><br/>Expiring</div>
          <div style="text-align:center"><strong style="font-size:24px;color:#dc2626">${(data.expiredCount || 0) + (data.missingCount || 0)}</strong><br/>Missing/Expired</div>
          ${data.exemptCount > 0 ? `<div style="text-align:center"><strong style="font-size:24px;color:#6b7280">${data.exemptCount}</strong><br/>Exempt</div>` : ""}
        </div>
        <p style="text-align:center;font-size:18px;color:#1e3a5f">${data.compliancePercentage}% Compliant (${data.compliantCount}/${data.totalStaff} staff)</p>
        <table style="width:100%;border-collapse:collapse;margin-top:16px">
          <thead><tr style="background:#1e3a5f;color:white">
            <th style="padding:10px 12px;text-align:left">Staff Name</th>
            <th style="padding:10px 12px;text-align:left">Role</th>
            <th style="padding:10px 12px;text-align:left">Director's Date</th>
            <th style="padding:10px 12px;text-align:left">Expires</th>
            <th style="padding:10px 12px;text-align:left">Status</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </body></html>
    `;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    w.print();
  };

  if (loading) return <div className="flex items-center justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /><span className="ml-2 text-sm text-gray-600">Loading staff evaluation status...</span></div>;
  if (error) return <div className="p-4 text-red-600 bg-red-50 rounded">{error}</div>;
  if (!data) return null;

  const statusOrder = { missing: 0, expired: 1, expiring: 2, compliant: 3, exempt: 4 };
  const sorted = [...data.staff].sort((a, b) => (statusOrder[a.status] || 0) - (statusOrder[b.status] || 0));

  return (
    <div className="space-y-4" data-testid="staff-evaluation-summary">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
          <Users className="w-5 h-5" /> Staff Evaluation Compliance
        </h3>
        <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700" data-testid="print-eval-summary">
          <Printer className="w-4 h-4" /> Print Report
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-700">{data.compliantCount}</div>
          <div className="text-xs text-green-600">Current</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-amber-700">{data.expiringCount || 0}</div>
          <div className="text-xs text-amber-600">Expiring</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-red-700">{(data.expiredCount || 0) + (data.missingCount || 0)}</div>
          <div className="text-xs text-red-600">Missing / Expired</div>
        </div>
        {data.exemptCount > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-600">{data.exemptCount}</div>
            <div className="text-xs text-gray-500">Exempt</div>
          </div>
        )}
      </div>

      <div className="text-center text-sm font-medium" style={{ color: data.compliancePercentage === 100 ? "#16a34a" : data.compliancePercentage >= 80 ? "#d97706" : "#dc2626" }}>
        {data.compliancePercentage}% Compliant ({data.compliantCount}/{data.totalStaff} staff)
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="eval-summary-table">
          <thead>
            <tr className="bg-blue-900 text-white text-left">
              <th className="px-3 py-2">Staff Name</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Director's Date</th>
              <th className="px-3 py-2">Expires</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => (
              <tr key={s.staffId} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{s.staffName}</td>
                <td className="px-3 py-2 text-gray-600">{s.role || "Staff"}</td>
                <td className="px-3 py-2">{s.evaluationDate || "N/A"}</td>
                <td className="px-3 py-2">{s.expirationDate || "N/A"}</td>
                <td className="px-3 py-2">
                  {s.status === "compliant" && <span className="inline-flex items-center gap-1 text-green-600 font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> Current</span>}
                  {s.status === "expiring" && <span className="inline-flex items-center gap-1 text-amber-600 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> Expiring</span>}
                  {s.status === "expired" && <span className="inline-flex items-center gap-1 text-red-600 font-medium"><XCircle className="w-3.5 h-3.5" /> Expired</span>}
                  {s.status === "missing" && <span className="inline-flex items-center gap-1 text-red-600 font-medium"><XCircle className="w-3.5 h-3.5" /> Missing</span>}
                  {s.status === "exempt" && <span className="inline-flex items-center gap-1 text-gray-500 font-medium"><ShieldOff className="w-3.5 h-3.5" /> Exempt</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StaffEvaluationSummaryTab;
